import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { useSupabaseLoader } from "../../../../../server/supabase/hooks";
import { useFollowUpScheduleText } from "../../../../follow-up/hooks/use-follow-up-schedule-text";
import {
  type FollowUp,
  type MomentExistingFollowUps,
} from "../../../../follow-up/server/follow-up-service";
import { convertMomentScheduleToFollowUpSchedule } from "../../../../follow-up/server/moment-to-follow-up";
import type { FollowUpMomentDetails } from "../../../../follow-up/server/types";
import { statusToPill } from "../../../../moment-card/utils";

export const ShowFollowUpMomentsDialog: FC<{
  open: boolean;
  onClose(): void;
  data: MomentExistingFollowUps;
  onCreateFollowUp(followUpData: FollowUpMomentDetails): void;
}> = ({ open, onClose, data, onCreateFollowUp }) => {
  const { t } = useTranslation();

  const { data: followUpData, loading: followUpLoading } = useSupabaseLoader(
    async ({ supabase, account_id }) => {
      const { data: existingData, error } = await supabase
        .rpc("follow_up_moment_details", {
          account_id_: account_id,
          moment_id_: data.id,
        })
        .maybeSingle();
      if (error) throw error;
      return existingData;
    },
    [data.id],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t("Follow-Ups for this Communication")}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          {followUpLoading ? (
            <Skeleton variant="rectangular" height={100} />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("Follow-Up name")}</TableCell>
                  <TableCell>{t("Follow-Up reason")}</TableCell>
                  <TableCell>{t("Schedule")}</TableCell>
                  <TableCell>{t("Status")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(followUpData?.existing_follow_ups ?? []).map((followUp) => (
                  <TableRow key={followUp.id}>
                    <TableCell>
                      <Stack direction="row" alignItems="center">
                        <Button
                          data-analytics-id="moment-form-follow-up-dialog-follow-up-moment-link"
                          href={`/follow-up/${followUp.id}`}
                          target="_blank"
                          endIcon={<OpenInNew />}
                        >
                          {followUp.title}
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <FollowUpReason config={followUp.config} surveys={followUpData?.surveys} />
                    </TableCell>
                    <TableCell>
                      {followUpData && (
                        <FollowUpSchedule followUp={followUp} moment={followUpData} />
                      )}
                    </TableCell>
                    <TableCell>{statusToPill(followUp.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Button
            data-analytics-id="moment-form-follow-up-dialog-create-follow-up-button"
            variant="outlined"
            onClick={() => followUpData && onCreateFollowUp(followUpData)}
            disabled={followUpLoading}
            loading={followUpLoading}
            sx={{ width: "fit-content" }}
            startIcon={<AddCircleOutline />}
          >
            {t("Create a new Follow-Up")}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          data-analytics-id="moment-form-follow-up-dialog-close-button"
          variant="text"
          onClick={onClose}
        >
          {t("Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FollowUpReason: FC<{
  config: FollowUp["config"];
  surveys?: FollowUpMomentDetails["surveys"];
}> = ({ config, surveys }) => {
  const { t } = useTranslation();

  if (!config) return null;

  switch (config.type) {
    case "no_interaction":
      return t("Hasn't seen communication");
    case "no_survey_completion": {
      const survey = surveys?.find((s) => s.id === config.survey_id);
      if (!survey) return t("Hasn't completed survey");
      return t("Hasn't completed survey: {{surveyName}}", { surveyName: survey.title });
    }
  }
};

const FollowUpSchedule: FC<{
  moment: FollowUpMomentDetails;
  followUp: MomentExistingFollowUps["follow_ups"][number];
}> = ({ moment, followUp }) => {
  const data = convertMomentScheduleToFollowUpSchedule(moment, {
    immediate: followUp.immediate ?? false,
    schedule: followUp.schedule,
    timezone: followUp.timezone ?? null,
  });

  return useFollowUpScheduleText(data.schedule, moment);
};
